import { createSelector, DefaultProjectorFn, MemoizedSelector } from '@ngrx/store';
import { GatewayType } from '../../../projects/client';

import { AppState } from '../app.state';
import { AuthState } from '../auth';
import { RemoteConfigState } from '../remote-config';

import {
  GatewayCommissioningAvailability,
  GATEWAY_COMMISSIONING_STEP,
  GATEWAY_COMMISSIONING_DEVICE,
  GATEWAY_COMMISSIONING_FLOW,
  TransferInstallationFormData,
  GatewayStateEnum,
} from './gateway-commissioning.model';
import { GatewayCommissioningState } from './gateway-commissioning.state';

export namespace GatewayCommissioningSelectors {
  export const gatewayCommissioningState = (state: AppState): GatewayCommissioningState =>
    state.gatewayCommissioning;
  export const remoteConfigState = (state: AppState): RemoteConfigState => state.remoteConfig;
  export const authState = (state: AppState): AuthState => state.auth;

  export function getState(): MemoizedSelector<
    AppState,
    GatewayCommissioningState,
    DefaultProjectorFn<GatewayCommissioningState>
  > {
    return createSelector(gatewayCommissioningState, (state: GatewayCommissioningState) => state);
  }

  export function getTransferInstallationLoading(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      gatewayCommissioningState,
      (state: GatewayCommissioningState) => state.transferInstallationLoading
    );
  }

  export function getSelectedDevice(): MemoizedSelector<
    AppState,
    GATEWAY_COMMISSIONING_DEVICE,
    DefaultProjectorFn<GATEWAY_COMMISSIONING_DEVICE>
  > {
    return createSelector(
      gatewayCommissioningState,
      (state: GatewayCommissioningState) => state.selectedDevice
    );
  }

  export function getCommissioningFlow(): MemoizedSelector<
    AppState,
    GATEWAY_COMMISSIONING_FLOW,
    DefaultProjectorFn<GATEWAY_COMMISSIONING_FLOW>
  > {
    return createSelector(
      gatewayCommissioningState,
      (state: GatewayCommissioningState) => state.commissioningFlow
    );
  }

  export function getActiveStep(): MemoizedSelector<
    AppState,
    GATEWAY_COMMISSIONING_STEP,
    DefaultProjectorFn<GATEWAY_COMMISSIONING_STEP>
  > {
    return createSelector(
      gatewayCommissioningState,
      (state: GatewayCommissioningState) => state.activeStep
    );
  }

  export function getEmailAddress(): MemoizedSelector<
    AppState,
    string,
    DefaultProjectorFn<string>
  > {
    return createSelector(
      gatewayCommissioningState,
      (state: GatewayCommissioningState) => state.email
    );
  }
  export const selectGatewayTags = createSelector(
    gatewayCommissioningState,
    (state: GatewayCommissioningState): string[] => state.gatewayVerificationData?.tags
  );

  export function isAvailable(): MemoizedSelector<
    AppState,
    GatewayCommissioningAvailability,
    DefaultProjectorFn<GatewayCommissioningAvailability>
  > {
    return createSelector(
      remoteConfigState,
      authState,
      (rcState: RemoteConfigState, aState: AuthState) => {
        const isGatewayCommissioningAvailable = rcState.remoteConfig.gatewayCommissioningAvailable;
        const isMaintainer = aState.authorizedAs.maintainer;
        const isLoading =
          isGatewayCommissioningAvailable === undefined || isMaintainer === undefined;
        const status: GatewayCommissioningAvailability = {
          isLoading,
          isAvailable: isGatewayCommissioningAvailable && isMaintainer,
        };

        return status;
      }
    );
  }

  export function getFormData(): MemoizedSelector<
    AppState,
    TransferInstallationFormData,
    DefaultProjectorFn<TransferInstallationFormData>
  > {
    return createSelector(gatewayCommissioningState, (state: GatewayCommissioningState) => ({
      gateway: state.gateway,
      address: state.address,
      email: state.email,
      description: state.description,
      heatingType: state.heatingType,
      installationOwnerData: state.installationOwnerData,
      isGrandmaFunctionSelected: state.isGrandmaFunctionSelected,
      isOwnerOfTheInstallationSelected: state.isOwnerOfTheInstallationSelected,
    }));
  }

  export function isGatewayOfType(
    gatewayType: GatewayType
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      gatewayCommissioningState,
      (state: GatewayCommissioningState) =>
        !state.gatewayVerificationLoading &&
        state.gatewayVerificationData?.gatewayType === gatewayType
    );
  }

  export function isGatewayVerificationOngoing(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      gatewayCommissioningState,
      (state: GatewayCommissioningState) => state.gatewayVerificationLoading
    );
  }

  export function isAnyFormControlFilledOut(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      gatewayCommissioningState,
      (state: GatewayCommissioningState) =>
        !!state.gateway.serial ||
        !!state.gateway.password ||
        !!state.address.street ||
        !!state.address.houseNumber ||
        !!state.address.zip ||
        !!state.address.city ||
        !!state.address.country ||
        !!state.installationOwnerData.firstName ||
        !!state.installationOwnerData.lastName ||
        !!state.email
    );
  }

  export function selectGatewayVerificationError(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      gatewayCommissioningState,
      (state: GatewayCommissioningState) => !!state.gatewayVerificationError
    );
  }

  export function isGatewayVerificationDataRegistered(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      gatewayCommissioningState,
      (state: GatewayCommissioningState) =>
        state.gatewayVerificationData &&
        state.gatewayVerificationData.gatewayState === GatewayStateEnum.Registered
    );
  }
}
